import { encode as base64_encode } from "base-64";
import { FC, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useGetReportAllFieldsQuery, useRunReportQuery } from "../../../api/network/reportApiServices";
import AdminTable from "../../../components/adminTable";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG, REPORT_CATEGORIES, REPORT_TYPES } from "../../../constants/constants";
import { useAccountTimezone } from "../../../hooks/timezone";
import useConversion from "../../../hooks/conversion";
import { REPORT_FIELD_OUTPUT_TYPES } from "../../reports/constants";
import { routeUrls } from "../../../navigation/routeUrls";
import { formattedDateTimeBasedOnTimezone, getStartEndOfDayInUTC, jsTimeToDBTime } from "../../../utils/dateTime";
import { useLoggedInUserData } from "../../../utils/user";
import { IncidentDetailResponse } from "./type";
import { toast } from "react-toastify";

type IncidentDetailEventReportProps = {
  incident: IncidentDetailResponse;
  printMode?: boolean;
  onLoadComplete?: () => void;
};

// "Asset Detail" standard report - one row per event for the asset/day
const EVENT_REPORT_ID = 7;

const IncidentDetailEventReport: FC<IncidentDetailEventReportProps> = (
  { incident, printMode = false, onLoadComplete }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.incident.incident_review.detailsPage.widget_event_report",
  });
  const navigate = useNavigate();
  const thisUserTimezone = useLoggedInUserData("timezone");
  const { tzForDateFns } = useAccountTimezone();
  const { convertToUnit, getUnitByOutputType } = useConversion();
  const hasCalledLoadComplete = useRef(false);

  const incidentDateInTimezone = formattedDateTimeBasedOnTimezone(
    incident.timestamp * 1000,
    tzForDateFns,
    "yyyy-MM-dd"
  );
  const { startOfDayInUTC, endOfDayInUTC } = getStartEndOfDayInUTC(incidentDateInTimezone, tzForDateFns);

  const {
    data: dataReportFields,
    isFetching: isFetchingReportFields,
    isError: isErrorReportFields,
  } = useGetReportAllFieldsQuery({
    report: EVENT_REPORT_ID,
    page: 1,
    page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
    search: "",
    is_custom: true,
  });
  const orderedFields = useMemo(
    () => [...(dataReportFields?.results || [])].sort((a, b) => a.custom_order - b.custom_order),
    [dataReportFields?.results]
  );

  // start 2 minutes before the incident instead of midnight, so the report
  // leads into the incident rather than showing the entire day up to that point
  const reportStartTime = jsTimeToDBTime((incident.timestamp - 120) * 1000);

  const { data, isFetching, isError: isErrorRunReport } = useRunReportQuery(
    {
      id: EVENT_REPORT_ID.toString(),
      device_id: incident.deviceid,
      start_time: reportStartTime,
      end_time: jsTimeToDBTime(endOfDayInUTC.getTime()),
      page: 1,
      page_size: 15,
      search: "",
    },
    { skip: !incident.deviceid }
  );

  // column label with unit suffix (e.g. "Odometer (mi)") to match the account's
  // configured preference - same convention as the standalone Reports page
  const columnLabels = useMemo(
    () =>
      orderedFields.map((field) => {
        const unit = getUnitByOutputType(field.output_type as REPORT_FIELD_OUTPUT_TYPES);
        return unit ? `${field.name} (${unit})` : field.name;
      }),
    [orderedFields]
  );
  useEffect(() => {
    if (printMode && !isFetching && !hasCalledLoadComplete.current) {
      handleExportData();
      onLoadComplete?.();
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          "===== INCIDENT DETAIL EVENT REPORT LOADED",
          new Date().getTime()
        );
      hasCalledLoadComplete.current = true;
    }
  }, [printMode, isFetching, onLoadComplete]);

  const handleExportData = () => {
    if (tableData.length === 0)
      return;
    try {
      if (tableData.length === 0)
        toast.info(t("event_report_empty"));
      else {
        const headers = orderedFields.map((field) => field.name);
        const rows = tableData.map((row) => row.cellData);
        const csvContent = [
          headers,
          ...rows,
        ]
          .map((row) =>
            row
              .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
              .join(",")
          )
          .join("\n");

        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "incident-event-report.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success(t("event_report_csv_successful"));
      }

    } catch (error) {
      toast.success(t("event_report_csv_failed"));
    }
  }

  const tableData: TAdminTableRowData[] = useMemo(() => {
    return (data?.results || []).map((row: any) => ({
      navLink: "",
      cellData: orderedFields.map((field) => {
        const rawValue = row[field.id];
        if (rawValue === null || rawValue === undefined) return "-";
        const { finalData } = convertToUnit(field.output_type as REPORT_FIELD_OUTPUT_TYPES, rawValue);
        return finalData === "" || finalData === null || finalData === undefined ? "-" : finalData;
      }),
    }));
  }, [data?.results, orderedFields]);

  const handleViewAllAsReport = () => {
    const filterValues = {
      device_id: incident.deviceid,
      ds_time_range: "",
      start_time: reportStartTime,
      end_time: jsTimeToDBTime(endOfDayInUTC.getTime()),
      timezone: thisUserTimezone || "",
      time_range: "",
      device_id_all: false,
      device_id_status_filter: true,
      device_id_all_filter: true,
      nonce: Date.now(),
    };
    const reportFiltersB64 = base64_encode(JSON.stringify(filterValues)).replace(/=/g, "");

    const reportPageUrl =
      routeUrls.dashboardChildren.reportsChildren.reportPrefix +
      routeUrls.dashboardChildren.reportsChildren.reportSuffixParamsUri
        .replace(":reportType", REPORT_TYPES.STANDARD)
        .replace(":categoryId", REPORT_CATEGORIES.ASSET_DETAIL)
        .replace(":reportId", EVENT_REPORT_ID.toString())
        .replace(":reportFilters", reportFiltersB64);

    navigate(reportPageUrl);
  };

  return (
    <div className="print:hidden">
      <div className="flex flex-col bg-white rounded-xl border border-heading-gray888/20 shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-[15px] text-heading-black">{t("heading")}</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleViewAllAsReport}
              className="h-7 px-3 rounded-md border border-accent-blue-dark text-accent-blue-dark text-xs font-semibold hover:bg-accent-blue-pale transition whitespace-nowrap"
            >
              {t("view_all")}
            </button>
          </div>
        </div>
        {isErrorRunReport || isErrorReportFields ? (
          <div className="h-64 rounded-lg bg-gray-100 flex items-center justify-center">
            <p className="text-heading-gray666 text-sm px-8 text-center">{t("fetch_error")}</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto rounded-lg">
            <AdminTable
              columns={columnLabels}
              data={tableData}
              isLoading={isFetching || isFetchingReportFields}
              compact={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default IncidentDetailEventReport;
